import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import DescriptionIcon from '@mui/icons-material/Description';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Link } from 'react-router-dom';

export default function HelpCenterPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'How do I add my first contractor?',
          a: 'Navigate to Payees from the sidebar menu, click "Add New Payee", and fill in their information including name, contact details, and hourly rate. You can also specify if they are an individual or business entity.',
        },
        {
          q: 'What information do I need to collect from contractors?',
          a: "You'll need their legal name, address, Tax ID (SSN or EIN), and payment details. This information is required to generate accurate 1099 forms at tax time.",
        },
        {
          q: 'How do I create my first timesheet?',
          a: 'Go to Timesheets, select a payee, choose the week ending date, and enter hours worked for each day. Click "Submit Timesheet" to save. The system automatically calculates gross pay based on the hourly rate.',
        },
      ],
    },
    {
      category: 'Billing & Subscriptions',
      questions: [
        {
          q: 'Can I change my subscription plan?',
          a: "Yes! Go to Profile → Subscription Management to upgrade or downgrade your plan. Changes are prorated, and you'll see the exact charge or credit before confirming.",
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit and debit cards through Stripe, our secure payment processor. We do not store your card information.',
        },
        {
          q: 'Do you offer refunds?',
          a: 'Yes, we offer a full refund within 14 days of your initial purchase, no questions asked. After 14 days, we prorate refunds based on unused time.',
        },
        {
          q: 'What happens if my subscription expires?',
          a: "If payment fails or your subscription expires, you'll have read-only access to your data for 30 days. You can reactivate anytime during this period to restore full access.",
        },
      ],
    },
    {
      category: 'Timesheets & Payroll',
      questions: [
        {
          q: 'Can I edit a submitted timesheet?',
          a: 'Yes, you can edit timesheets at any time before generating payment records. Simply go to Timesheets, find the entry, and click Edit.',
        },
        {
          q: 'What is the difference between flat rate and hourly?',
          a: 'Hourly contractors are paid based on hours worked × hourly rate. Flat rate contractors receive a fixed payment regardless of hours (though you can still track hours for records).',
        },
        {
          q: 'How do I track multiple projects for one contractor?',
          a: 'Each timesheet row can have a task description and notes. Use the notes field to specify project names or details for each entry.',
        },
        {
          q: 'Does SimplyPayroll process actual payments?',
          a: 'No, SimplyPayroll is a tracking tool only. It calculates amounts owed but does not transfer money. You are responsible for paying contractors through your preferred method (check, ACH, etc.).',
        },
      ],
    },
    {
      category: '1099 Forms & Tax',
      questions: [
        {
          q: 'When do I need to generate 1099 forms?',
          a: '1099-MISC forms are required for contractors who earned $600 or more during the calendar year. The IRS deadline is January 31st for providing forms to contractors and filing with the IRS.',
        },
        {
          q: 'Does SimplyPayroll file 1099s with the IRS?',
          a: 'No, SimplyPayroll generates the PDF forms, but you are responsible for filing them with the IRS and providing copies to contractors. We recommend using the IRS FIRE system for electronic filing.',
        },
        {
          q: 'What if a contractor earned less than $600?',
          a: 'You are not required to file a 1099 for contractors who earned less than $600 in a calendar year, though you should still maintain records of all payments.',
        },
        {
          q: 'Can I regenerate a 1099 if I made a mistake?',
          a: 'Yes, you can regenerate 1099 forms at any time. However, if you\'ve already filed with the IRS, you\'ll need to file a corrected 1099 (1099-MISC marked as "CORRECTED").',
        },
      ],
    },
    {
      category: 'Security & Privacy',
      questions: [
        {
          q: 'How do you protect sensitive data like SSNs?',
          a: 'All sensitive data (SSN, EIN, Tax IDs) is encrypted at rest using AES-256 encryption and transmitted over secure HTTPS connections. Only authorized users can decrypt this information.',
        },
        {
          q: 'Can I export my data?',
          a: 'Yes, you can export all your data including contractor information, timesheets, and payment records from the Reports section. Data is available in CSV format.',
        },
        {
          q: 'What happens to my data if I cancel?',
          a: 'You have 30 days to download your data after cancellation. After 30 days, data is permanently deleted from active systems. Backup copies are retained for 90 days for disaster recovery.',
        },
        {
          q: 'Who can access my business data?',
          a: 'Only users you invite to your business account can access your data. You can set role-based permissions (Admin or User) to control what team members can see and edit.',
        },
      ],
    },
  ];

  const filteredFaqs = faqs.map((category) => ({
    ...category,
    questions: category.questions.filter(
      (faq) =>
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  }));

  return (
    <Box
      sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 8 }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: 'linear-gradient(135deg, #1E23FE 0%, #0D9E6F 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Help Center
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.secondary', mb: 4 }}>
            Find answers to common questions and get help with SimplyPayroll
          </Typography>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search for help..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 600, mx: 'auto' }}
          />
        </Box>

        {/* Quick Links */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} md={4}>
            <Card
              component={Link}
              to="/docs"
              sx={{
                textDecoration: 'none',
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon
                  sx={{ fontSize: 48, color: 'primary.main', mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Documentation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Step-by-step guides and tutorials
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              component={Link}
              to="/contact"
              sx={{
                textDecoration: 'none',
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <SupportAgentIcon
                  sx={{ fontSize: 48, color: 'success.main', mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Contact Support
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get help from our support team
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              component={Link}
              to="/status"
              sx={{
                textDecoration: 'none',
                height: '100%',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <QuestionAnswerIcon
                  sx={{ fontSize: 48, color: 'info.main', mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  System Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check service availability
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ Sections */}
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          Frequently Asked Questions
        </Typography>

        {filteredFaqs.map(
          (category) =>
            category.questions.length > 0 && (
              <Box key={category.category} sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}
                >
                  {category.category}
                </Typography>
                {category.questions.map((faq, index) => (
                  <Accordion key={index} elevation={1}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 500 }}>{faq.q}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {faq.a}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )
        )}

        {/* Still Need Help */}
        <Box
          sx={{
            mt: 8,
            p: 4,
            bgcolor: 'primary.50',
            borderRadius: 3,
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'primary.200',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Still need help?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Can't find what you're looking for? Our support team is here to
            help.
          </Typography>
          <Button
            component={Link}
            to="/contact"
            variant="contained"
            size="large"
            sx={{ minWidth: 200 }}
          >
            Contact Support
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
